import { useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useBadgeRulesQuery } from '@/client/api/badges/badges.query';
import {
  useCreateBadgeRuleMutation,
  useDeleteBadgeRuleMutation,
  useUpdateBadgeRuleMutation,
} from '@/client/api/badges/badges.mutation';
import type { BadgeRule } from '@/client/types/badges';

interface BadgeRulesPanelProps {
  btid: number;
}

const emptyForm = {
  metric: '',
  operator: 'gte',
  threshold: '',
  label: '',
};

const BadgeRulesPanel = ({ btid }: BadgeRulesPanelProps) => {
  const { data: rules = [], isLoading } = useBadgeRulesQuery(btid);
  const createMutation = useCreateBadgeRuleMutation();
  const updateMutation = useUpdateBadgeRuleMutation();
  const deleteMutation = useDeleteBadgeRuleMutation();

  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState<BadgeRule | null>(null);
  const [showForm, setShowForm] = useState(false);

  const isPending =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const reset = () => {
    setForm(emptyForm);
    setEditing(null);
    setShowForm(false);
  };

  const startEdit = (rule: BadgeRule) => {
    setEditing(rule);
    setForm({
      metric: String(rule.metric ?? ''),
      operator: String(rule.operator ?? 'gte'),
      threshold: String(rule.threshold ?? ''),
      label: String(rule.label ?? ''),
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.metric.trim() || form.threshold.trim() === '') {
      toast.error('Metric and threshold are required');
      return;
    }

    const toastId = toast.loading(editing ? 'Updating rule…' : 'Creating rule…');
    try {
      if (editing) {
        await updateMutation.mutateAsync({
          brid: editing.brid,
          btid,
          metric: form.metric.trim(),
          operator: form.operator,
          threshold: form.threshold,
          label: form.label.trim() || undefined,
        });
        toast.success('Rule updated', { id: toastId });
      } else {
        await createMutation.mutateAsync({
          btid,
          metric: form.metric.trim(),
          operator: form.operator,
          threshold: form.threshold,
          label: form.label.trim() || undefined,
        });
        toast.success('Rule created', { id: toastId });
      }
      reset();
    } catch (error: any) {
      toast.error(editing ? 'Update failed' : 'Create failed', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
      });
    }
  };

  const handleDelete = async (rule: BadgeRule) => {
    const toastId = toast.loading('Deleting rule…');
    try {
      await deleteMutation.mutateAsync({ brid: rule.brid, btid });
      toast.success('Rule deactivated', { id: toastId });
    } catch (error: any) {
      toast.error('Delete failed', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
      });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Auto-award rules</h3>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={() => {
            setEditing(null);
            setForm(emptyForm);
            setShowForm(true);
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          Add rule
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : rules.length === 0 && !showForm ? (
        <p className="text-sm text-gray-500">No rules for this badge type.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {rules.map((rule) => {
            const inactive = rule.active === 0 || rule.active === false;
            return (
              <li
                key={rule.brid}
                className={`flex items-center justify-between gap-3 px-3 py-2.5 ${
                  inactive ? 'opacity-50' : ''
                }`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {rule.label || rule.metric || `Rule ${rule.brid}`}
                  </p>
                  <p className="text-xs text-gray-500">
                    {rule.metric} {rule.operator} {rule.threshold}
                    {inactive ? ' · inactive' : ''}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    disabled={isPending}
                    onClick={() => startEdit(rule)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="text-red-600"
                    disabled={isPending || inactive}
                    onClick={() => void handleDelete(rule)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showForm ? (
        <form
          onSubmit={handleSubmit}
          className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
        >
          <p className="text-xs font-medium text-gray-600">
            {editing ? `Edit rule brid ${editing.brid}` : 'New rule'}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="rule-metric">Metric</Label>
              <Input
                id="rule-metric"
                value={form.metric}
                onChange={(e) => setForm((p) => ({ ...p, metric: e.target.value }))}
                placeholder="e.g. listing_count"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rule-operator">Operator</Label>
              <select
                id="rule-operator"
                value={form.operator}
                onChange={(e) => setForm((p) => ({ ...p, operator: e.target.value }))}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <option value="gte">gte (≥)</option>
                <option value="lte">lte (≤)</option>
                <option value="eq">eq (=)</option>
                <option value="gt">gt (&gt;)</option>
                <option value="lt">lt (&lt;)</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="rule-threshold">Threshold</Label>
              <Input
                id="rule-threshold"
                value={form.threshold}
                onChange={(e) => setForm((p) => ({ ...p, threshold: e.target.value }))}
                placeholder="10"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rule-label">Label</Label>
              <Input
                id="rule-label"
                value={form.label}
                onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                placeholder="Optional display label"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={reset} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {editing ? 'Save' : 'Create'}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
};

export default BadgeRulesPanel;
